import { useEffect, useState, type Dispatch } from 'react';
import debounce from 'lodash/debounce';
import { getJQLForCollection } from '@atlassian/jira-polaris-component-collections/src/common/utils/jql.tsx';
import { getIdeasCountForCollection } from '../../../services/ideas/index.tsx';

type IdeaCountRequest = Promise<number> | undefined;

const debouncedGetIdeasCountWithJQL = debounce(
	(
		jql: string,
		setRequest: Dispatch<IdeaCountRequest>,
		res: (count: number) => void,
		rej?: (err: Error) => void,
	) => {
		const request = getIdeasCountForCollection(jql);
		setRequest(request);
		request.then(res, rej).finally(() => setRequest(undefined));
	},
	500,
);

export const useCollectionIdeasCount = (
	projectKeys: string[],
	filtersJql: string,
): [undefined | number, boolean, Error | null, IdeaCountRequest] => {
	const jql = getJQLForCollection(projectKeys, filtersJql);
	const [ideasCount, setIdeasCount] = useState<undefined | number>(undefined);
	const [isLoading, setLoading] = useState(false);
	const [error, setError] = useState<null | Error>(null);
	const [request, setRequest] = useState<IdeaCountRequest>(undefined);

	useEffect(() => {
		if (jql && projectKeys.length) {
			setLoading(true);
			debouncedGetIdeasCountWithJQL(
				jql,
				setRequest,
				(newCount) => {
					setIdeasCount(newCount);
					setLoading(false);
					setError(null);
				},
				(err: Error) => {
					setIdeasCount(undefined);
					setLoading(false);
					setError(err);
				},
			);
		} else {
			setIdeasCount(undefined);
		}
	}, [jql, projectKeys.length]);

	return [ideasCount, isLoading, error, request];
};
